import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Handshake, Tag, Inbox, Plus, Trash2 } from "lucide-react";

const chf = (c) => `${(c / 100).toLocaleString("fr-CH")} CHF`;
const LEAD_STATUSES = ["new", "contacted", "discussion", "proposal_sent", "accepted", "refused", "archived"];
const STATUS_FR = { new: "Nouveau", contacted: "Contacté", discussion: "Discussion", proposal_sent: "Proposition envoyée", accepted: "Accepté", refused: "Refusé", archived: "Archivé" };

export default function PartnersManager() {
  const qc = useQueryClient();
  const { data: pricing = [] } = useQuery({ queryKey: ["adminPricing"], queryFn: async () => (await api.get("/admin/pricing")).data });
  const { data: leads = [] } = useQuery({ queryKey: ["adminLeads"], queryFn: async () => (await api.get("/admin/partners/leads")).data });
  const { data: partners = [] } = useQuery({ queryKey: ["adminPartners"], queryFn: async () => (await api.get("/admin/partners")).data });

  const [np, setNp] = useState({ name: "", tier: "partner", category: "partner", logo: "", website: "", description: "", active: true });
  const inp = "bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 outline-none focus:border-amber-500/50";

  const savePrice = async (key, amount, max_slots, active) => {
    try { await api.put(`/admin/pricing/${key}`, { amount: Number(amount), max_slots: max_slots === "" || max_slots == null ? null : Number(max_slots), active }); toast.success("Tarif enregistré"); qc.invalidateQueries({ queryKey: ["adminPricing"] }); qc.invalidateQueries({ queryKey: ["pricing"] }); }
    catch { toast.error("Erreur"); }
  };
  const setLeadStatus = async (id, status) => {
    try { await api.put(`/admin/partners/leads/${id}`, { status }); toast.success("Statut mis à jour"); qc.invalidateQueries({ queryKey: ["adminLeads"] }); }
    catch { toast.error("Erreur"); }
  };
  const addPartner = async () => {
    if (!np.name.trim()) { toast.error("Nom requis"); return; }
    try { await api.post("/admin/partners", np); toast.success("Partenaire ajouté"); setNp({ name: "", tier: "partner", category: "partner", logo: "", website: "", description: "", active: true }); qc.invalidateQueries({ queryKey: ["adminPartners"] }); qc.invalidateQueries({ queryKey: ["partnersPublic"] }); }
    catch { toast.error("Erreur"); }
  };
  const delPartner = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    try { await api.delete(`/admin/partners/${id}`); qc.invalidateQueries({ queryKey: ["adminPartners"] }); qc.invalidateQueries({ queryKey: ["partnersPublic"] }); }
    catch { toast.error("Erreur"); }
  };

  return (
    <section className="mt-16" data-testid="partners-manager">
      <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><Handshake className="w-5 h-5 text-amber-500" /> Partenaires & tarifs</h2>

      {/* Pricing */}
      <div className="mb-10">
        <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Tarifs (source unique)</h3>
        <div className="space-y-2" data-testid="pricing-list">
          {pricing.map((p) => <PriceRow key={p.key} p={p} onSave={savePrice} inp={inp} chf={chf} />)}
        </div>
      </div>

      {/* Leads */}
      <div className="mb-10">
        <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-3 flex items-center gap-2"><Inbox className="w-4 h-4" /> Demandes de partenariat ({leads.length})</h3>
        {leads.length === 0 ? <p className="text-stone-500 text-sm" data-testid="leads-empty">Aucune demande.</p> : (
          <div className="space-y-2" data-testid="leads-list">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-[#121418] border border-[#272A34] rounded-lg p-4">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-stone-100">{l.company} <span className="text-xs text-stone-500">· {l.plan || "—"}</span></div>
                  <div className="text-xs text-stone-500">{l.first_name} {l.last_name} · {l.email} · {l.phone || "—"}</div>
                  {l.message && <div className="text-sm text-stone-400 mt-1 line-clamp-2">{l.message}</div>}
                </div>
                <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} className={inp}>
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{STATUS_FR[s]}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Partners CRUD */}
      <div>
        <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-3">Partenaires validés (publics)</h3>
        <div className="flex flex-wrap gap-2 mb-4 bg-[#121418] border border-[#272A34] rounded-lg p-4" data-testid="partner-add-form">
          <input className={inp} placeholder="Nom" data-testid="partner-add-name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} />
          <select className={inp} data-testid="partner-add-tier" value={np.tier} onChange={(e) => setNp({ ...np, tier: e.target.value })}>
            <option value="principal">Principal</option><option value="founder">Fondateur</option><option value="partner">Partenaire</option>
          </select>
          <input className={inp} placeholder="URL logo" value={np.logo} onChange={(e) => setNp({ ...np, logo: e.target.value })} />
          <input className={inp} placeholder="Site web" value={np.website} onChange={(e) => setNp({ ...np, website: e.target.value })} />
          <input className={`${inp} flex-1 min-w-[180px]`} placeholder="Description" value={np.description} onChange={(e) => setNp({ ...np, description: e.target.value })} />
          <button onClick={addPartner} data-testid="partner-add-btn" className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm"><Plus className="w-4 h-4" /> Ajouter</button>
        </div>
        {partners.length === 0 ? <p className="text-stone-500 text-sm" data-testid="partners-admin-empty">Aucun partenaire.</p> : (
          <div className="space-y-2" data-testid="partners-admin-list">
            {partners.map((p) => (
              <div key={p.id} className="flex items-center gap-3 bg-[#121418] border border-[#272A34] rounded-lg p-3">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-amber-950/40 text-amber-300 border border-amber-800/40">{p.tier}</span>
                <span className="font-serif text-stone-100 flex-1 truncate">{p.name}</span>
                {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-500 truncate max-w-[160px]">{p.website}</a>}
                <button onClick={() => delPartner(p.id)} data-testid={`partner-del-${p.id}`} className="text-stone-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const PriceRow = ({ p, onSave, inp, chf }) => {
  const [amount, setAmount] = useState(p.amount);
  const [slots, setSlots] = useState(p.max_slots ?? "");
  const [active, setActive] = useState(p.active !== false);
  return (
    <div className="flex flex-wrap items-center gap-2 bg-[#121418] border border-[#272A34] rounded-lg p-3" data-testid={`price-row-${p.key}`}>
      <span className="font-serif text-stone-100 min-w-[180px]">{p.name} <span className="text-xs text-stone-500 font-mono">/{p.interval}</span></span>
      <label className="text-xs text-stone-500">Montant (cts)</label>
      <input className={`${inp} w-28`} type="number" data-testid={`price-amount-${p.key}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
      <span className="text-xs text-amber-400 font-mono">{chf(Number(amount) || 0)}</span>
      <label className="text-xs text-stone-500 ml-2">Places max</label>
      <input className={`${inp} w-20`} type="number" data-testid={`price-slots-${p.key}`} value={slots} onChange={(e) => setSlots(e.target.value)} placeholder="∞" />
      <label className="flex items-center gap-1.5 text-xs text-stone-400"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-amber-500" /> Actif</label>
      <button onClick={() => onSave(p.key, amount, slots, active)} data-testid={`price-save-${p.key}`} className="bg-[#181A20] border border-[#272A34] hover:border-amber-500/50 text-stone-200 px-3 py-1.5 rounded-md text-xs">Enregistrer</button>
    </div>
  );
};
